import { removeDiacritics } from "../lib/text";

// minimum length of both stored tokens and query parts
// search field requires at least this many characters
export const MIN_TOKEN_LENGTH = 3;

// prefix for reversed numeric tokens (suffix search); keeps them separate from forward tokens
export const REVERSED_TOKEN_PREFIX = '~';

// mongo filter for documents that still need searchTokens backfill (missing or null; [] is skipped)
export const missingSearchTokensFilter = {
   searchTokens: null,
};

// lowercase + remove diacritics, but keep dashes and other characters
// (do NOT use fullNormalizeForSearch, it strips dashes/underscores)
export const normalizeForToken = (text = "") =>
  removeDiacritics(String(text)).toLowerCase();

export const reverseString = (str) => str.split("").reverse().join("");

const escapeRegExp = (str) =>
  str.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

const isAllDigits = (str) => /^\d+$/.test(str);

// generate the prefix-searchable tokens for a single normalized word
const tokensForWord = (word) => {
  const tokens = [];

  if (word.length >= MIN_TOKEN_LENGTH) tokens.push(word);

  // numeric tail: from first digit to end of the word (e.g. "12345-6789")
  const firstDigitIndex = word.search(/\d/);
  if (firstDigitIndex !== -1) {
    const tail = word.slice(firstDigitIndex);
    if (tail.length >= MIN_TOKEN_LENGTH && tail !== word) tokens.push(tail);
  }

  // each maximal digit run + its reverse (numbers only)
  const digitRuns = word.match(/\d+/g) || [];
  digitRuns.forEach((run) => {
    if (run.length >= MIN_TOKEN_LENGTH) {
      tokens.push(run, REVERSED_TOKEN_PREFIX + reverseString(run));
    }
  });

  return tokens;
};

// build the deduped array of search tokens from the searchable order fields
export const buildSearchTokens = ({
  clientName,
  reference,
  manufacturerId,
} = {}) => {
  const tokens = new Set();

  [clientName, reference, manufacturerId].filter(Boolean).forEach((field) => {
    normalizeForToken(field)
      .split(/\s+/)
      .filter(Boolean)
      .forEach((word) => {
        tokensForWord(word).forEach((token) => tokens.add(token));
      });
  });

  return Array.from(tokens);
};

// build a Mongo match object that searches the indexed searchTokens array
// using anchored prefix regexes (no `i` flag) so the multikey index can be used
// returns null when there is no usable query part (input too short)
export const buildSearchTokenQuery = (input = "") => {
  const parts = normalizeForToken(input)
    .split(/\s+/)
    .filter((part) => part.length >= MIN_TOKEN_LENGTH);

  if (parts.length === 0) return null;

  const partToQuery = (part) => {
    const branches = [{ searchTokens: new RegExp(`^${escapeRegExp(part)}`) }];
    // namespaced reversed query enables end-of-number search without false positives
    if (isAllDigits(part)) {
      branches.push({
        searchTokens: new RegExp(
          `^${escapeRegExp(REVERSED_TOKEN_PREFIX + reverseString(part))}`,
        ),
      });
    }
    return { $or: branches };
  };

  if (parts.length === 1) return partToQuery(parts[0]);

  return { $and: parts.map(partToQuery) };
};
